import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as topojson from 'topojson-client';
import { Bert } from 'meteor/themeteorchef:bert';
import { includes, isEmpty, assign, toNumber, get, find, hasIn, map, keys } from 'lodash';
import { Row, Col, Form, Button, InputGroup } from 'react-bootstrap';
import turf from '@turf/turf';

import * as layerOps from '../../../api/Layers/layer-operations';
import { LAYER_SUBTYPES } from '../../../api/Layers/Layers';
import * as styleOps from '../../../api/Styles/style-operations';
import {
  DEFAULT,
  SYMBOLOGY_TYPES,
  STYLE_TYPES,
  getStyleType,
  POINT_TYPES,
  GEOJSON_GEOMETRIES
} from '../../../api/Styles/Styles';

import adaptURL from '../../../modules/adapt-url';
import { coordinatesToXYZ } from '../../../modules/coordinates-to-xyz';
import getGeoJSONFromCSV from '../../../modules/get-geojson-from-csv';
import getGeoJSONFromExcel from '../../../modules/get-geojson-from-excel';
import getGeoJSONFromMapBox from '../../../modules/get-geojson-from-mapbox';
import getGeoJSONURLFromMapbox from '../../../modules/get-geojson-url-from-mapbox';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import joinGeoJSONThematicLayer from '../../../modules/join-geojson-thematic-layers';
import loadGeoJSONFromURL from '../../../modules/load-geojson-from-url';
import loadGeoJSONIndoorFromURL from '../../../modules/load-geojson-indoor-from-url';
import loadStyleFromURL from '../../../modules/load-style-from-url';
import methodPromise from '../../../modules/method-promise-wrapper';
import { parseGeoJSONProperties } from '../../../modules/parse-geojson-properties';

import SaveButton from '../../atoms/SaveButton';
import LayerRasterField from '../../atoms/LayerRasterField';
import ModalMap from '../../atoms/ModalMap';
import BlankState from '../BlankState';
import GeoJSONStyleBuilder from '../GeoJSONStyleBuilder';
import Validation from '../../containers/Validation';

const NEGATIVE_Y_PARAM_REGEX = new RegExp(/{-y}/);

const comingSoonMessage = styleType => {
  switch (styleType) {
    case LAYER_SUBTYPES.EXTERNAL_SERVER:
      return 'Here you will be able to set up a connection to an external server';
    case LAYER_SUBTYPES.UPLOADED_FILE:
      return 'Here you will be able to upload a layer file';
    default:
      return '';
  }
};

class StyleEditor extends Component {
  state = {
    defaultMapCenter: isEmpty(this.props.userControlSetting.defaultMapCenter)
      ? this.props.userControlSetting.defaultMapCenter
      : layerOps.getMapLocation(this.props.layer),
    geojson: {},
    isLoading: false,
    loadedStyle: {},
    showMapBoxModal: false,
    styleType:
      styleOps.getType(this.props.style) ||
      (layerOps.isJoin(this.props.layer)
        ? getStyleType(LAYER_SUBTYPES.GEOJSON)
        : getStyleType(layerOps.getSubType(this.props.layer)))
  };

  geojsonStyleComponent = React.createRef();

  async componentDidMount() {
    const { geojson, styleType } = this.state;
    if (isEmpty(geojson) && includes([STYLE_TYPES.GEOJSON, STYLE_TYPES.MAPBOX], styleType))
      await this.loadData();
  }

  changeDefaultCenter = newDefaultMapCenter =>
    this.setState({ defaultMapCenter: newDefaultMapCenter });

  createStyle = form => {
    const { layer } = this.props;
    const { styleType } = this.state;
    const newStyle = { styleType };

    if (includes([STYLE_TYPES.GEOJSON, STYLE_TYPES.MAPBOX], styleType)) {
      const geojsonStyleState = get(this, 'geojsonStyleComponent.current.state');
      const symbologyType = form.symbologyType.value.trim();
      newStyle.geojsonStyle = {
        type: symbologyType,
        geometry: layerOps.getGeoJSONGeometry(layer)
      };

      if (layerOps.isGeoJSONVT(layer) || layerOps.isMapBox(layer))
        assign(newStyle.geojsonStyle, { tolerance: toNumber(form.tolerance.value) });

      if (
        layerOps.getGeoJSONGeometry(layer) === GEOJSON_GEOMETRIES.POINT &&
        geojsonStyleState.pointType === POINT_TYPES.ICON &&
        isEmpty(form.iconClass.value)
      ) {
        Bert.alert('No Font Aweome Icon specified', 'warning');
        return '';
      }

      const isDefaultChecked = form.defaultColor ? form.defaultColor.checked : false;
      switch (symbologyType) {
        case SYMBOLOGY_TYPES.SINGLE:
          assign(newStyle.geojsonStyle, {
            styleData: {
              color: isDefaultChecked ? DEFAULT : geojsonStyleState.color,
              alpha: geojsonStyleState.alpha,
              weight: form.weight ? toNumber(form.weight.value) : 1,
              iconClass: form.iconClass ? form.iconClass.value.trim() : '',
              borderColor: geojsonStyleState.borderColor,
              borderAlpha: geojsonStyleState.borderAlpha,
              borderStyle: form.lineType ? form.lineType.value : '',
              pointSize: form.pointSize ? toNumber(form.pointSize.value) : 5,
              svgCategoryId: form.svgCategory ? form.svgCategory.value : '',
              svgUrl: form.svgUrl ? form.svgUrl.value : '',
              isScaledSymbology: get(form, ['scaledSymbology', 'checked'], false)
            }
          });
          if (
            !layerOps.isMapBox(layer) &&
            !layerOps.isGeoJSONVT(layer) &&
            layerOps.getGeoJSONGeometry(layer) === GEOJSON_GEOMETRIES.POINT
          )
            assign(newStyle.geojsonStyle.styleData, { pointType: form.pointType.value.trim() });
          break;

        case SYMBOLOGY_TYPES.CATEGORIZED:
        case SYMBOLOGY_TYPES.NUMERIC:
          if (isEmpty(form.propName.value)) {
            Bert.alert('No GeoJSON attribute selected', 'warning');
            return '';
          }
          if (isEmpty(form.colorRangeValues.value)) {
            Bert.alert('No color range selected', 'warning');
            return '';
          }
          if (symbologyType === SYMBOLOGY_TYPES.NUMERIC && isEmpty(form.numIntervals.value)) {
            Bert.alert('No number of intervals defined', 'warning');
            return '';
          }
          if (isEmpty(geojsonStyleState.legend)) {
            Bert.alert('The legend is empty', 'warning');
            return '';
          }
          assign(newStyle.geojsonStyle, {
            propName: form.propName.value,
            styleData: {
              legend: geojsonStyleState.legend,
              colorRange: form.colorRangeValues.value,
              alpha: toNumber(form.alphaSelector.value),
              weight: form.weight ? toNumber(form.weight.value) : 1,
              iconClass: form.iconClass ? form.iconClass.value.trim() : '',
              borderColor: geojsonStyleState.borderColor,
              borderAlpha: geojsonStyleState.borderAlpha,
              borderStyle: form.lineType ? form.lineType.value : '',
              pointSize: form.pointSize ? toNumber(form.pointSize.value) : 5,
              svgCategoryId: form.svgCategory ? form.svgCategory.value : '',
              svgUrl: form.svgUrl ? form.svgUrl.value : '',
              isScaledSymbology: get(form, ['scaledSymbology', 'checked'], false)
            }
          });
          if (
            !layerOps.isMapBox(layer) &&
            !layerOps.isGeoJSONVT(layer) &&
            layerOps.getGeoJSONGeometry(layer) === GEOJSON_GEOMETRIES.POINT
          )
            assign(newStyle.geojsonStyle.styleData, { pointType: form.pointType.value.trim() });

          if (symbologyType === SYMBOLOGY_TYPES.NUMERIC)
            assign(newStyle.geojsonStyle, {
              numIntervals: Number(form.numIntervals.value),
              dataLimits: geojsonStyleState.dataLimits
            });
          break;

        default:
          throw new Error('Symbology type not recognized');
      }
    }

    if (styleType === STYLE_TYPES.RASTER) {
      newStyle.rasterStyle = { opacity: toNumber(form.opacity.value) };
      if (layerOps.isWMS(layer) && hasIn(form, 'externalSLD'))
        assign(newStyle.rasterStyle, { sld: form.externalSLD.value });
    }

    if (styleType === STYLE_TYPES.CUSTOM)
      assign(newStyle, {
        styleURL: form.styleURL.value.trim(),
        isScaledCustom: get(form, ['isScaledCustom', 'checked'], false)
      });

    return newStyle;
  };

  handleSubmit = async form => {
    const { style, layer } = this.props;

    const existingStyle = !isEmpty(styleOps.getId(style));
    const methodToCall = existingStyle ? 'styles.update' : 'styles.insert';
    const layerId = layerOps.getId(layer);

    const newStyle = this.createStyle(form);
    newStyle.name = form.name.value.trim();
    if (existingStyle) newStyle._id = styleOps.getId(style);

    try {
      const styleId = await methodPromise(methodToCall, newStyle);
      await methodPromise('layers.updateStyle', layerId, styleId);
      this.redirectToLayerView(form, existingStyle, layerId);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  hideMapBoxModal = async () => {
    const { layer } = this.props;
    const { defaultMapCenter } = this.state;
    const url = adaptURL(layerOps.getURL(layer));
    const mapboxLayerName = layerOps.getMapBoxLayerName(layer);
    const layerTileParams = coordinatesToXYZ(defaultMapCenter);
    if (url.match(NEGATIVE_Y_PARAM_REGEX)) {
      layerTileParams['-y'] = layerTileParams.y;
      delete layerTileParams.y;
    }
    try {
      const geojson = await getGeoJSONFromMapBox({ layerTileParams, mapboxLayerName, url });
      this.setState({ showMapBoxModal: false, isLoading: false, geojson });
    } catch (error) {
      handleErrorInClient('Please select another point');
    }
  };

  loadData = async () => {
    const { layer } = this.props;
    let geojson = {};
    let mapboxLayerName;
    let geojsonUrl;
    let layerName;
    let topojsonFile;
    let objectKey;
    const url = adaptURL(layerOps.getURL(layer));
    const annotationProperties = layerOps.getAnnotationLayerProperties(layer).geojsonAnnotations;
    const geojsonFeatures =
      !isEmpty(annotationProperties) &&
      annotationProperties.features.map(item => {
        const newName = item.properties.template.icon;
        return { ...item, properties: { template: newName } };
      });

    this.setState({ isLoading: true });
    try {
      switch (true) {
        case layerOps.isAnnotationsLayer(layer):
        case layerOps.isAnnotationsRealTime(layer):
          geojson = { type: 'FeatureCollection', features: geojsonFeatures };
          break;
        case layerOps.isGeoJSON(layer):
        case layerOps.isGeoJSONVT(layer):
        case layerOps.isRealTime(layer):
          geojson = await loadGeoJSONFromURL(url);
          break;
        case layerOps.isIndoor(layer):
          geojson = await loadGeoJSONIndoorFromURL(url);
          break;
        case layerOps.isJoin(layer):
          geojson = await joinGeoJSONThematicLayer({
            geojsonId: layerOps.getGeoJSONId(layer),
            thematicId: layerOps.getThematicId(layer),
            geojsonPropToJoin: layerOps.getGeoJSONPropToJoin(layer),
            thematicKeyToJoin: layerOps.getThematicKeyToJoin(layer),
            sorensenDiceMinimun: layerOps.getSorensenDiceMinimun(layer),
            layerSubType: layerOps.getSubType(layer)
          });
          break;
        case layerOps.isMapBox(layer):
          mapboxLayerName = layerOps.getMapBoxLayerName(layer);
          ({ adaptedUrl: geojsonUrl, layerName } = getGeoJSONURLFromMapbox(url));
          if (!isEmpty(layerName)) {
            geojson = await loadGeoJSONFromURL(geojsonUrl);
          } else {
            const defaultMapCenter = isEmpty(layerOps.getMapLocation(layer))
              ? turf.point([0, 0], { zoom: 0 })
              : layerOps.getMapLocation(layer);
            const layerTileParams = coordinatesToXYZ(defaultMapCenter);
            geojson = await getGeoJSONFromMapBox({ layerTileParams, mapboxLayerName, url });
          }
          break;
        case layerOps.isOGR(layer):
          geojson = await methodPromise('layers.loadOGRGeoJSON', {
            url,
            layerName: layerOps.getOGRLayerName(layer),
            attribute: layerOps.getOGRAttributes(layer).split(',')
          });
          break;
        case layerOps.isTopoJSON(layer):
          topojsonFile = await loadGeoJSONFromURL(url);
          [objectKey] = keys(topojsonFile.objects);
          geojson = topojson.feature(topojsonFile, topojsonFile.objects[objectKey]);
          break;
        case layerOps.isCSV(layer):
          geojson = await getGeoJSONFromCSV(layer);
          break;
        case layerOps.isExcel(layer):
        case layerOps.isGoogleSheets(layer):
          geojson = await getGeoJSONFromExcel(layer);
          break;
        case layerOps.isFiware(layer):
          geojson = await methodPromise('layers.loadFiware', {
            fiwareLayerName: layerOps.getFiwareLayerName(layer),
            georefType: layerOps.getGeorefType(layer),
            loadParameters: {
              ...layerOps.getFiwareParams(layer),
              fiwareTokenURL: layerOps.getURL(layer)
            },
            spatialTableFields: layerOps.getTableSpatialObject(layer)
          });
          break;
        case layerOps.isPostgreSQL(layer):
          geojson = await methodPromise('layers.loadPostgreSQL', {
            loadParameters: layerOps.getConnectionParams(layer),
            attributes: map(layerOps.getGeoJSONProps(layer), 'name')
          });
          break;
        default:
          break;
      }
      this.setState({ geojson, isLoading: false });
    } catch (error) {
      if (!isEmpty(mapboxLayerName)) {
        this.setState({ showMapBoxModal: true });
      } else {
        this.setState({ geojson: {}, isLoading: false });
        handleErrorInClient(error);
      }
    }
  };

  loadExternalStyle = async event => {
    const { layer } = this.props;
    const { styleType } = this.state;

    const url = adaptURL(event.target.form.styleURL.value.trim());
    try {
      const style = await loadStyleFromURL({ url, layer });
      if (hasIn(style, 'default') && hasIn(style, 'styles')) {
        if (styleType === STYLE_TYPES.RASTER) {
          Bert.alert('Custom Raster Styles Are Not Accepted', 'warning');
        } else {
          this.setState({ styleType: STYLE_TYPES.CUSTOM });
          Bert.alert('Custom style', 'success');
        }
      } else {
        this.setState({ loadedStyle: style });
        Bert.alert('Style Loaded', 'success');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  redirectToLayerView(form, existingStyle, layerId) {
    const { history } = this.props;
    const confirmation = existingStyle ? 'Style updated!' : 'Style added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/layers/${layerId}`);
  }

  render() {
    const { deployEnv, layer, style } = this.props;
    const {
      defaultMapCenter,
      geojson,
      isLoading,
      loadedStyle,
      showMapBoxModal,
      styleType
    } = this.state;

    const featureNotReady = !includes(STYLE_TYPES, styleType);
    const areButtonsDisabled = includes([STYLE_TYPES.GEOJSON, STYLE_TYPES.MAPBOX], styleType)
      ? featureNotReady || isEmpty(geojson)
      : featureNotReady;
    const buttonMessage = isEmpty(styleOps.getId(style)) ? 'Add Style' : 'Save Style';

    const getProps = () => {
      let properties;
      if (layerOps.isMapBox(layer)) {
        properties = layerOps.getMapBoxProps(layer);
      } else if (layerOps.isAnnotationsLayer(layer) || layerOps.isAnnotationsRealTime(layer)) {
        const featureList = layerOps.getAnnotationLayerProperties(layer).geojsonAnnotations
          .features;
        const propertiesWithValue = featureList.map(item => {
          const newName = item.properties.template.icon;
          return { ...item, properties: { template: newName } };
        });
        const { geojsonProperties } = parseGeoJSONProperties({
          type: 'FeatureCollection',
          features: propertiesWithValue
        });
        properties = geojsonProperties;
      } else {
        properties = layerOps.getGeoJSONProps(layer);
      }
      return properties;
    };
    const layerProps = getProps();
    const validationProps = {
      rules: {
        name: {
          required: true
        },
        styleType: {
          required: true
        },
        tolerance: {
          required: includes(
            [LAYER_SUBTYPES.GEOJSONVT, LAYER_SUBTYPES.MAPBOX],
            layerOps.getSubType(layer)
          )
        },
        pointType: {
          required: layerOps.getGeoJSONGeometry(layer) === GEOJSON_GEOMETRIES.POINT
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        },
        styleType: {
          required: 'Need a type'
        },
        tolerance: {
          required: 'Need to set a tolerance'
        },
        pointType: {
          required: 'Need to indicate a point type'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };

    const styleToShow = isEmpty(loadedStyle) ? style : loadedStyle;
    const geojsonGeometry = layerOps.getGeoJSONGeometry(layer);
    const isPointCustom =
      styleType === STYLE_TYPES.CUSTOM && geojsonGeometry === GEOJSON_GEOMETRIES.POINT;

    return (
      <Validation {...validationProps}>
        <Form onSubmit={event => event.preventDefault()}>
          <Row>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  defaultValue={styleOps.getName(styleToShow)}
                  placeholder="Oh, The Places You'll Go!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Style SubType</Form.Label>
                <Form.Control
                  name="styleType"
                  value={find(STYLE_TYPES.properties, ['value', styleType]).humanValue}
                  readOnly
                />
              </Form.Group>
              {featureNotReady && (
                <BlankState
                  icon={{ style: 'solid', symbol: 'cogs' }}
                  title="This feature is not ready yet. Coming Soon"
                  subtitle={comingSoonMessage(styleType)}
                />
              )}
              <SaveButton
                areButtonsDisabled={areButtonsDisabled}
                buttonMessage={buttonMessage}
                isLoading={isLoading}
              />
            </Col>
            <Col xs={12} md={8}>
              {!featureNotReady && (
                <>
                  {(isEmpty(styleOps.getName(style)) || styleType === STYLE_TYPES.CUSTOM) && (
                    <>
                      <Form.Group>
                        <Form.Label>Load External Style JSON</Form.Label>
                        <InputGroup className="mb-3">
                          <Form.Control
                            name="styleURL"
                            defaultValue={styleOps.getStyleURL(style)}
                            placeholder="Style URL"
                          />
                          <InputGroup.Append>
                            <Button onClick={this.loadExternalStyle} variant="outline-primary">
                              Load
                            </Button>
                          </InputGroup.Append>
                        </InputGroup>
                      </Form.Group>
                      {isPointCustom && (
                        <Form.Group>
                          <Form.Check
                            name="isScaledCustom"
                            label="Scaled Symbology"
                            defaultChecked={styleOps.isScaledSymbology(style)}
                          />
                        </Form.Group>
                      )}
                    </>
                  )}
                  <LayerRasterField styleType={styleType} style={styleToShow} />
                  {!isLoading && includes([STYLE_TYPES.GEOJSON, STYLE_TYPES.MAPBOX], styleType) && (
                    <GeoJSONStyleBuilder
                      geojson={geojson}
                      geojsonGeometry={geojsonGeometry}
                      geojsonProperties={layerProps}
                      hasTolerance={layerOps.isGeoJSONVT(layer) || layerOps.isMapBox(layer)}
                      ref={this.geojsonStyleComponent}
                      style={styleToShow}
                    />
                  )}
                </>
              )}
            </Col>
          </Row>
          <SaveButton
            areButtonsDisabled={areButtonsDisabled}
            buttonMessage={buttonMessage}
            isLoading={isLoading}
            isMdButton={false}
          />
          <ModalMap
            defaultMapCenter={defaultMapCenter}
            deployEnv={deployEnv}
            headerText="Select Point To Fetch"
            isShownMap={showMapBoxModal}
            hideMap={this.hideMapBoxModal}
            updateDefaultCenter={this.changeDefaultCenter}
          />
        </Form>
      </Validation>
    );
  }
}

StyleEditor.defaultProps = {
  style: { name: '', type: '' }
};

StyleEditor.propTypes = {
  deployEnv: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  layer: PropTypes.object.isRequired,
  style: PropTypes.object,
  userControlSetting: PropTypes.object.isRequired
};

export default StyleEditor;
